<ion-content class="user-admin-content">
  <ion-grid class="user-admin-grid">
    <ion-row class="user-admin-row">
      <ion-col class="user-admin-content-wrapper">
        <div class="user-admin-page">
          <div class="eyebrow">{{ isAdmin ? 'Admin Only' : 'Manager Workspace' }}</div>
          <ion-title>User Management</ion-title>
          <p class="page-intro">
            Use the standard roster to review and edit user accounts. Duplicate-account debugging remains admin-only,
            but managers can now open and update other user profiles from the same roster flow.
          </p>

          <div class="summary-grid" *ngIf="canManageUsers">
            <div class="summary-card">
              <span class="summary-label">Users</span>
              <span class="summary-value">{{ users.length }}</span>
            </div>
            <div class="summary-card" *ngIf="isAdmin">
              <span class="summary-label">Issue Users</span>
              <span class="summary-value">{{ usersWithIssuesCount }}</span>
            </div>
            <div class="summary-card" *ngIf="isAdmin">
              <span class="summary-label">Duplicate Groups</span>
              <span class="summary-value">{{ duplicateGroups.length }}</span>
            </div>
            <div class="summary-card" *ngIf="isAdmin">
              <span class="summary-label">Accounts in Groups</span>
              <span class="summary-value">{{ duplicateAccountCount }}</span>
            </div>
          </div>

          <div class="access-panel" *ngIf="!canManageUsers">
            Manager or admin access is required to review and manage user records.
          </div>

          <div class="status-panel" *ngIf="canManageUsers && isLoading">Loading user roster...</div>
          <div class="status-panel error" *ngIf="canManageUsers && loadError">{{ loadError }}</div>

          <div class="debug-banner" *ngIf="isAdmin && !isLoading && hasDuplicateIssues">
            <div>
              <div class="debug-banner-title">User issues detected</div>
              <div class="debug-banner-copy">
                {{ duplicateGroups.length }} duplicate login groups are currently flagged for admin review.
              </div>
            </div>
            <button type="button" class="section-button" (click)="openDebugPanel()">Review All Issues</button>
          </div>

          <div class="listing-controls" *ngIf="canManageUsers && !isLoading && !loadError">
            <app-listing-filter
              [options]="rosterColumns"
              (sortOptionSelectedEventEmitter)="handleSortOptionEvent($event)"
              (sortDirectionSelectedEventEmitter)="handleSortDirectionEvent($event)"
            >
            </app-listing-filter>
            <app-listing-search (searchFilterEventEmitted)="handleSearchFilterEvent($event)"></app-listing-search>
          </div>

          <table class="user-table" *ngIf="canManageUsers && !isLoading && !loadError && usersFiltered.length">
            <thead>
              <tr>
                <th>Name</th>
                <th>Username</th>
                <th>Email</th>
                <th>Status</th>
                <th>Issue</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              <tr
                *ngFor="let account of visibleUsers; trackBy: trackByUser"
                [class.inactive]="!account.userActive || account.deleted"
                [class.has-issue]="hasUserDebugIssue(account)"
              >
                <td class="listing-body-cell has-link" [routerLink]="['/users', account.userId]">
                  <span class="user-table-name">{{ getUserDisplayName(account) }}</span>
                </td>
                <td class="listing-body-cell">{{ account.username || 'n/a' }}</td>
                <td class="listing-body-cell">{{ account.userEmail || 'n/a' }}</td>
                <td class="listing-body-cell">{{ getUserStatusLabel(account) }}</td>
                <td class="listing-body-cell issue-cell">
                  <span class="issue-pill" [class.clear]="!hasUserDebugIssue(account)">{{
                    getDebugLabel(account)
                  }}</span>
                  <span class="issue-copy">{{ getDebugCountLabel(account) }}</span>
                </td>
                <td class="listing-body-cell action-cell">
                  <a class="row-action" [routerLink]="['/users', account.userId]">Edit User</a>
                  <button
                    *ngIf="isAdmin && hasUserDebugIssue(account)"
                    type="button"
                    class="row-action secondary"
                    (click)="openDebugPanel(getDuplicateGroupForUser(account.userId))"
                  >
                    Debug User
                  </button>
                </td>
              </tr>
            </tbody>
          </table>

          <jw-pagination
            *ngIf="canManageUsers && !isLoading && !loadError && usersFiltered.length"
            [items]="usersFiltered"
            (changePage)="onChangePage($event)"
          >
          </jw-pagination>

          <div class="status-panel" *ngIf="canManageUsers && !isLoading && !loadError && !usersFiltered.length">
            No users match the current roster filters.
          </div>

          <section class="debug-panel" id="user-debug-panel" *ngIf="isAdmin && debugPanelExpanded">
            <div class="section-header debug-panel-header">
              <div>
                <div class="eyebrow">Debug User</div>
                <h2>Duplicate Login Review</h2>
              </div>
              <div class="debug-actions">
                <button
                  type="button"
                  class="section-button secondary"
                  *ngIf="selectedDebugGroupKey"
                  (click)="openDebugPanel()"
                >
                  Show All Issues
                </button>
                <button type="button" class="section-button secondary" (click)="closeDebugPanel()">Close Panel</button>
              </div>
            </div>
            <p class="debug-panel-copy">
              Login resolution normalizes usernames and emails to the local part before matching local users. When more
              than one local account matches the same canonical key, the app prompts for account choice and this panel
              gives admins the merge-workup tools to resolve it.
            </p>

            <div class="group-jump-list" *ngIf="duplicateGroups.length > 1">
              <button
                type="button"
                class="group-jump-chip"
                *ngFor="let group of duplicateGroups; trackBy: trackByGroup"
                (click)="openDebugPanel(group)"
              >
                <span>{{ group.key }}</span>
                <span>{{ group.users.length }}</span>
              </button>
            </div>

            <div
              class="duplicate-group"
              *ngFor="let group of visibleDebugGroups; trackBy: trackByGroup"
              [attr.id]="getGroupSectionId(group)"
            >
              <div class="group-header">
                <div>
                  <div class="group-key">{{ group.key }}</div>
                  <div class="group-copy">
                    Suggested target stays highlighted. Preview the stored procedure invocation first, then run the
                    merge from a non-target account into the selected target once you have confirmed downstream ownership.
                  </div>
                </div>
                <div class="group-header-actions">
                  <div class="group-count">{{ group.users.length }} accounts</div>
                  <button type="button" class="section-button secondary" (click)="toggleGroup(group)">
                    {{ group.collapsed ? 'Expand Group' : 'Collapse Group' }}
                  </button>
                </div>
              </div>

              <div class="group-body" *ngIf="!group.collapsed">
                <div class="group-grid">
                  <div
                    class="user-card"
                    *ngFor="let account of group.users; trackBy: trackByUser"
                    [class.selected-target]="group.selectedTargetUserId === account.userId"
                  >
                    <button class="target-button" type="button" (click)="selectTarget(group, account.userId)">
                      {{ group.selectedTargetUserId === account.userId ? 'Keep Target' : 'Set As Target' }}
                    </button>
                    <div class="user-name">{{ getUserDisplayName(account) }}</div>
                    <div class="user-meta">
                      {{ account.username }}<span *ngIf="account.userEmail"> · {{ account.userEmail }}</span>
                    </div>
                    <div class="user-flags">
                      <span class="user-flag" *ngIf="group.suggestedTargetUserId === account.userId">Suggested</span>
                      <span class="user-flag muted">{{ getUserRoleLabel(account) }}</span>
                      <span class="user-flag muted">{{ getUserStatusLabel(account) }}</span>
                    </div>
                    <div class="user-meta">Modified {{ account.userModified || account.userCreated || 'n/a' }}</div>
                    <div class="user-card-actions">
                      <a class="row-action" [routerLink]="['/users', account.userId]">Edit User</a>
                      <button
                        class="merge-button"
                        type="button"
                        *ngIf="group.selectedTargetUserId !== account.userId"
                        [disabled]="group.mergeLoading"
                        (click)="generateMergeScript(group, account)"
                      >
                        {{ group.mergeLoading && group.mergeLoadingAction === 'preview' ? 'Generating...' : 'Preview Merge Workup' }}
                      </button>
                      <button
                        class="merge-button secondary"
                        type="button"
                        *ngIf="group.selectedTargetUserId !== account.userId"
                        [disabled]="group.mergeLoading"
                        (click)="executeMerge(group, account)"
                      >
                        {{ group.mergeLoading && group.mergeLoadingAction === 'execute' ? 'Running...' : 'Run Merge Now' }}
                      </button>
                    </div>
                  </div>
                </div>

                <div class="status-panel error" *ngIf="group.mergeError">{{ group.mergeError }}</div>
                <div class="merge-script-panel" *ngIf="group.mergeScript">
                  <div class="merge-script-header">
                    <div class="merge-script-title">Generated merge workup</div>
                    <div class="merge-script-copy">
                      Stored procedure invocation from the admin-only `/users/merge-script` endpoint. This remains a preview until you click `Run Merge Now`.
                    </div>
                  </div>
                  <ion-textarea [value]="group.mergeScript" readonly autoGrow="true"></ion-textarea>
                </div>

                <div class="merge-script-panel" *ngIf="group.mergeResult">
                  <div class="merge-script-header">
                    <div class="merge-script-title">Executed merge workup</div>
                    <div class="merge-script-copy">
                      {{ group.mergeResult.message }} Outcome: {{ group.mergeResult.transactionOutcome }}.
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>
        </div>
      </ion-col>
    </ion-row>
  </ion-grid>
</ion-content>
